import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Drawer,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Badge,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  X,
  MapPin,
  Check,
  TrendingUp,
  Trash2,
  RefreshCw,
  FileText,
  Eye,
  Plus,
  Minus,
  Download,
  Link2,
  Loader2,
  Search,
} from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';
import { loadModules } from 'esri-loader';
import { keyframes } from '@mui/material/styles';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const InsightDrawer = ({ opportunity, onClose, onUpdate }) => {
  const { getAccessToken } = useAuth();
  const [compCount, setCompCount] = useState(5);
  const [showCompDetail, setShowCompDetail] = useState(false);
  const [selectedCompDetail, setSelectedCompDetail] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [activeComps, setActiveComps] = useState([]);
  const [showAddCustomComp, setShowAddCustomComp] = useState(false);
  const [customCompSearch, setCustomCompSearch] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [customCompTab, setCustomCompTab] = useState('list');
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [nearbyBuildings, setNearbyBuildings] = useState([]);
  const [rentGridId, setRentGridId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreComps, setHasMoreComps] = useState(false);
  const [totalCompsCount, setTotalCompsCount] = useState(0);
  const [nearbyBuildingsPage, setNearbyBuildingsPage] = useState(1);
  const [hasMoreNearbyBuildings, setHasMoreNearbyBuildings] = useState(false);
  const [loadingMoreNearbyBuildings, setLoadingMoreNearbyBuildings] =
    useState(false);
  const [totalNearbyBuildingsCount, setTotalNearbyBuildingsCount] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedBuildingToAdd, setSelectedBuildingToAdd] = useState(null);
  const [addingComp, setAddingComp] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [compToReplace, setCompToReplace] = useState(null);
  const [checkingMatchingUnits, setCheckingMatchingUnits] = useState(false);
  const [matchingUnitsData, setMatchingUnitsData] = useState(null);
  const [statistics, setStatistics] = useState({
    medianRent: 0,
    percentile25: 0,
    percentile75: 0,
  });
  const [sourceBuilding, setSourceBuilding] = useState(null);
  const [pdfLink, setPdfLink] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [showRemoveConfirmDialog, setShowRemoveConfirmDialog] = useState(false);
  const [compToRemove, setCompToRemove] = useState(null);
  const [removingComp, setRemovingComp] = useState(false);

  const calculateSimilarity = comp => {
    const sourceTotalUnits = opportunity.totalUnits || 0;
    const compTotalUnits = comp.totalUnits || 0;
    const compDistance = comp.distance || 0;

    if (sourceTotalUnits === 0 && compTotalUnits === 0 && compDistance === 0) {
      return 0.5;
    }

    const maxDistance = 10;
    const distanceScore = Math.max(0, 1 - compDistance / maxDistance);

    let unitsScore = 0.5;
    if (sourceTotalUnits > 0 && compTotalUnits > 0) {
      const unitsDiff = Math.abs(sourceTotalUnits - compTotalUnits);
      const unitsPercentDiff =
        unitsDiff / Math.max(sourceTotalUnits, compTotalUnits);
      unitsScore = Math.max(0, 1 - unitsPercentDiff);
    } else if (sourceTotalUnits === 0 && compTotalUnits === 0) {
      unitsScore = 1;
    }

    const similarity = distanceScore * 0.6 + unitsScore * 0.4;
    return Math.max(0, Math.min(1, similarity));
  };

  useEffect(() => {
    if (opportunity) {
      setCurrentPage(1);
      setComps([]);
      fetchCompsDetails(1, true);
    }
  }, [opportunity]);

  const fetchCompsDetails = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      const accessToken = getAccessToken();

      let url;
      if (opportunity.rentGridId) {
        url = new URL(
          `${process.env.REACT_APP_NODE_URL}/comps/details?rentGridId=${opportunity.rentGridId}`,
        );
      } else {
        url = new URL(
          `${process.env.REACT_APP_NODE_URL}/comps/details?buildingId=${
            opportunity.buildingId || ''
          }&bedrooms=${opportunity.bed}&bathrooms=${opportunity.bath}`,
        );
      }
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', '20');

      if (reset || page === 1) {
        url.searchParams.append('nearbyBuildingsPage', '1');
        url.searchParams.append('nearbyBuildingsLimit', '20');
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status && data.data) {
        const fetchedComps = data.data.comps || [];

        if (reset) {
          setComps(fetchedComps);
          setActiveComps(fetchedComps.slice(0, 5).map(c => c.id));
          setCompCount(Math.min(5, fetchedComps.length));
        } else {
          setComps(prev => [...prev, ...fetchedComps]);
        }

        setCurrentPage(page);
        setHasMoreComps(data.data.pagination?.hasMore || false);
        setTotalCompsCount(data.data.pagination?.total || 0);
        if (reset || page === 1) {
          setNearbyBuildings(data.data.nearbyBuildings || []);
          setRentGridId(data.data.rentGridId || null);
          setNearbyBuildingsPage(1);
          setHasMoreNearbyBuildings(
            data.data.nearbyBuildingsPagination?.hasMore || false,
          );
          setTotalNearbyBuildingsCount(
            data.data.nearbyBuildingsPagination?.total || 0,
          );
        }
        setStatistics(
          data.data.statistics || {
            medianRent: 0,
            percentile25: 0,
            percentile75: 0,
            totalCompsCount: 0,
          },
        );
        setSourceBuilding(data.data.sourceBuilding || null);
      } else {
        throw new Error(data.message || 'Failed to fetch comps details');
      }
    } catch (err) {
      console.error('Error fetching comps details:', err);
      setError(err.message || 'Failed to fetch comps details');
      if (reset) {
        setComps([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreComps = () => {
    if (!loadingMore && hasMoreComps) {
      fetchCompsDetails(currentPage + 1, false);
    }
  };

  const loadMoreNearbyBuildings = async () => {
    if (!loadingMoreNearbyBuildings && hasMoreNearbyBuildings && rentGridId) {
      try {
        setLoadingMoreNearbyBuildings(true);
        const accessToken = getAccessToken();

        let url;
        if (opportunity.rentGridId) {
          url = new URL(
            `${process.env.REACT_APP_NODE_URL}/comps/details?rentGridId=${opportunity.rentGridId}`,
          );
        } else {
          url = new URL(
            `${process.env.REACT_APP_NODE_URL}/comps/details?buildingId=${
              opportunity.buildingId || ''
            }&bedrooms=${opportunity.bed}&bathrooms=${opportunity.bath}`,
          );
        }
        url.searchParams.append('page', '1');
        url.searchParams.append('limit', '20');
        url.searchParams.append(
          'nearbyBuildingsPage',
          (nearbyBuildingsPage + 1).toString(),
        );
        url.searchParams.append('nearbyBuildingsLimit', '20');

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.status && data.data) {
          const newNearbyBuildings = data.data.nearbyBuildings || [];
          setNearbyBuildings(prev => [...prev, ...newNearbyBuildings]);
          setNearbyBuildingsPage(prev => prev + 1);
          setHasMoreNearbyBuildings(
            data.data.nearbyBuildingsPagination?.hasMore || false,
          );
        }
      } catch (err) {
        console.error('Error loading more nearby buildings:', err);
      } finally {
        setLoadingMoreNearbyBuildings(false);
      }
    }
  };

  const handleNearbyBuildingsScroll = e => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (
      scrollPercentage > 0.9 &&
      hasMoreNearbyBuildings &&
      !loadingMoreNearbyBuildings
    ) {
      loadMoreNearbyBuildings();
    }
  };

  const existingCompBuildingIds = [...new Set(comps.map(c => c.buildingId))];

  const filteredProperties = nearbyBuildings.filter(building => {
    const matchesSearch = building.address
      .toLowerCase()
      .includes(customCompSearch.toLowerCase());
    const isAlreadyComp = existingCompBuildingIds.includes(building.id);
    if (replaceMode && compToReplace) {
      return (
        matchesSearch &&
        building.id !== compToReplace.buildingId &&
        !isAlreadyComp
      );
    }
    return matchesSearch && !isAlreadyComp;
  });

  const medianRent = statistics.medianRent || 0;
  const percentile25 = statistics.percentile25 || 0;
  const percentile75 = statistics.percentile75 || 0;

  const recommendedRent =
    statistics.medianRent || opportunity.recommendedRent || 0;

  const history = [];

  const handleRemoveComp = comp => {
    setCompToRemove(comp);
    setShowRemoveConfirmDialog(true);
  };

  const handleReplaceComp = comp => {
    setCompToReplace(comp);
    setReplaceMode(true);
    setCustomCompTab('list');
    setNearbyBuildingsPage(1);
    setShowAddCustomComp(true);
  };

  const handleScroll = e => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (scrollPercentage > 0.9 && hasMoreComps && !loadingMore && !loading) {
      loadMoreComps();
    }
  };

  const handleAdjustCompCount = delta => {
    const newCount = Math.max(3, Math.min(comps.length, compCount + delta));

    if (newCount > compCount) {
      const nextComp = comps.find(c => !activeComps.includes(c.id));
      if (nextComp) {
        setActiveComps(prev => [...prev, nextComp.id]);
      }
    } else if (newCount < compCount) {
      setActiveComps(prev => prev.slice(0, -1));
    }

    setCompCount(newCount);
  };

  const handleApprove = () => {
    setShowReportModal(true);
    setReportGenerating(false);
    setReportReady(false);
    setPdfLink(null);
  };

  const handleGenerateReport = async () => {
    if (!opportunity) {
      alert('No opportunity selected for report generation.');
      return;
    }

    setReportGenerating(true);
    setReportReady(false);
    setPdfLink(null);

    try {
      const accessToken = getAccessToken();
      const requestBody = {
        rentGridId: rentGridId,
        opportunity: {
          building: opportunity.building,
          unit: opportunity.unit,
          bed: opportunity.bed,
          bath: opportunity.bath,
          sqft: opportunity.sqft,
          currentRent: opportunity.currentRent,
          recommendedRent: recommendedRent,
          delta: opportunity.delta,
          deltaPercent: opportunity.deltaPercent,
        },
      };

      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/rent-advisor-pdf/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If JSON parsing fails, use the default error message
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.status && data.link) {
        setPdfLink(data.link);
        setReportGenerating(false);
        setReportReady(true);
      } else {
        throw new Error(data.message || 'Failed to generate PDF');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setReportGenerating(false);
      alert(
        err.message ||
          'Failed to generate PDF report. Please ensure the server has been restarted.',
      );
    }
  };

  const checkMatchingUnitsForBuilding = async building => {
    if (!rentGridId) {
      return null;
    }

    try {
      setCheckingMatchingUnits(true);
      const accessToken = getAccessToken();

      const url = new URL(
        `${process.env.REACT_APP_NODE_URL}/comps/check-matching-units`,
      );
      url.searchParams.append('rentGridId', rentGridId);
      url.searchParams.append('buildingId', building.id);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status) {
        return data.data;
      } else {
        throw new Error(data.message || 'Failed to check matching units');
      }
    } catch (err) {
      console.error('Error checking matching units:', err);
      return null;
    } finally {
      setCheckingMatchingUnits(false);
    }
  };

  const handleAddCustomComp = async building => {
    setSelectedBuildingToAdd(building);
    setMatchingUnitsData(null);
    setShowConfirmDialog(true);

    const matchingData = await checkMatchingUnitsForBuilding(building);
    setMatchingUnitsData(matchingData);
  };

  const handleSelectBuildingForReplace = async building => {
    setSelectedBuildingToAdd(building);
    setMatchingUnitsData(null);
    setShowConfirmDialog(true);

    const matchingData = await checkMatchingUnitsForBuilding(building);
    setMatchingUnitsData(matchingData);
  };

  const confirmAddCustomComp = async () => {
    if (!selectedBuildingToAdd || !rentGridId) {
      return;
    }

    try {
      setAddingComp(true);
      const accessToken = getAccessToken();

      const requestBody = {
        rentGridId: rentGridId,
        buildingId: selectedBuildingToAdd.id,
      };

      if (replaceMode && compToReplace) {
        requestBody.replaceCompId = compToReplace.id;
        requestBody.replaceBuildingId = compToReplace.buildingId;
      }

      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/comps/custom-comp`,
        {
          method: replaceMode ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status) {
        setShowConfirmDialog(false);
        setShowAddCustomComp(false);
        setSelectedBuildingToAdd(null);
        setCompToReplace(null);
        setReplaceMode(false);
        setMatchingUnitsData(null);
        await fetchCompsDetails(1, true);
        if (onUpdate) {
          onUpdate();
        }
      } else {
        throw new Error(
          data.message ||
            `Failed to ${replaceMode ? 'replace' : 'add'} custom comp`,
        );
      }
    } catch (err) {
      console.error(
        `Error ${replaceMode ? 'replacing' : 'adding'} custom comp:`,
        err,
      );
      alert(
        err.message ||
          `Failed to ${replaceMode ? 'replace' : 'add'} custom comp`,
      );
    } finally {
      setAddingComp(false);
    }
  };

  const confirmRemoveComp = async () => {
    if (!compToRemove || !rentGridId) {
      return;
    }

    try {
      setRemovingComp(true);
      const accessToken = getAccessToken();

      const requestBody = {
        rentGridId: rentGridId,
        buildingId: compToRemove.buildingId,
      };

      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/comps/custom-comp`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status) {
        setShowRemoveConfirmDialog(false);
        setCompToRemove(null);
        await fetchCompsDetails(1, true);
        if (onUpdate) {
          onUpdate();
        }
      } else {
        throw new Error(data.message || 'Failed to remove custom comp');
      }
    } catch (err) {
      console.error('Error removing custom comp:', err);
      alert(err.message || 'Failed to remove custom comp');
    } finally {
      setRemovingComp(false);
    }
  };

  const displayedComps = comps.filter(c => activeComps.includes(c.id));

  return (
    <>
      <Drawer
        anchor='right'
        open={true}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '560px' },
            maxWidth: '560px',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              padding: '1.5rem',
              borderBottom: '1px solid #E5E7EB',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                justifyContent: 'space-between',
                marginBottom: '1rem',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {opportunity.building || 'N/A'} – Unit{' '}
                  {opportunity.unit || 'N/A'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginTop: '0.25rem',
                  }}
                >
                  {opportunity.bed ?? 'N/A'} BR · {opportunity.bath ?? 'N/A'} BA
                  {opportunity.sqft
                    ? ` · ${opportunity.sqft} sqft`
                    : ' · N/A sqft'}
                </Typography>
              </Box>
              <IconButton onClick={onClose} size='small'>
                <X size={20} />
              </IconButton>
            </Box>

            <Box
              sx={{
                padding: '1rem',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6B7280',
                    }}
                  >
                    Current Rent
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: '#111827',
                    }}
                  >
                    {opportunity.currentRent
                      ? `$${opportunity.currentRent.toLocaleString()}`
                      : 'N/A'}
                  </Typography>
                </Box>
                <TrendingUp size={24} color='#4FA490' />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6B7280',
                    }}
                  >
                    Recommended
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: '#4FA490',
                    }}
                  >
                    {recommendedRent
                      ? `$${recommendedRent.toLocaleString()}`
                      : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Button
              variant='contained'
              fullWidth
              onClick={handleApprove}
              startIcon={<FileText size={16} />}
              sx={{
                backgroundColor: '#39585C',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#2A3F42' },
              }}
            >
              Generate Report
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                borderBottom: '1px solid #E5E7EB',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#7B8A8B',
                  '&.Mui-selected': {
                    color: '#39585C',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#39585C',
                },
              }}
            >
              <Tab label='Comps' />
              <Tab label='Details' />
              {/* <Tab label="History" /> */}
            </Tabs>

            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                padding: '1.5rem',
              }}
              onScroll={handleScroll}
            >
              {loading && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingY: '4rem',
                  }}
                >
                  <CircularProgress sx={{ color: '#39585C' }} />
                </Box>
              )}

              {error && !loading && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingY: '4rem',
                    gap: '1rem',
                  }}
                >
                  <Typography sx={{ color: '#EF4444', fontSize: '0.875rem' }}>
                    {error}
                  </Typography>
                  <Button
                    variant='outlined'
                    onClick={fetchCompsDetails}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#D1D5DB',
                      color: '#374151',
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              )}

              {!loading && !error && activeTab === 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.5rem',
                  }}
                >
                  <Box
                    sx={{
                      height: '192px',
                      borderRadius: '0.5rem',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <CompsMap
                      sourceBuilding={sourceBuilding}
                      comps={comps}
                      getAccessToken={getAccessToken}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Typography sx={{ color: '#6B7280' }}>
                        Median Rent
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: '#111827' }}>
                        {medianRent ? `$${medianRent}` : 'N/A'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Typography sx={{ color: '#6B7280' }}>
                        25th Percentile
                      </Typography>
                      <Typography sx={{ color: '#6B7280' }}>
                        {percentile25 ? `$${percentile25}` : 'N/A'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Typography sx={{ color: '#6B7280' }}>
                        75th Percentile
                      </Typography>
                      <Typography sx={{ color: '#6B7280' }}>
                        {percentile75 ? `$${percentile75}` : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#111827',
                        }}
                      >
                        {comps.length} of{' '}
                        {statistics.totalCompsCount || comps.length} Comparable
                        Units
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => {
                            setCustomCompTab('list');
                            setNearbyBuildingsPage(1);
                            setShowAddCustomComp(true);
                          }}
                          startIcon={<Plus size={12} />}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            borderColor: '#D1D5DB',
                            color: '#374151',
                          }}
                        >
                          Add Custom Comp
                        </Button>
                      </Box>
                    </Box>

                    {comps.length === 0 && !loading && (
                      <Box
                        sx={{
                          textAlign: 'center',
                          paddingY: '2rem',
                          color: '#6B7280',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.875rem' }}>
                          No comparable units found
                        </Typography>
                      </Box>
                    )}

                    {comps.map(comp => (
                      <Box
                        key={comp.id}
                        sx={{
                          padding: '1rem',
                          border: '1px solid #E5E7EB',
                          borderRadius: '0.5rem',
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'start',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#111827',
                                  }}
                                >
                                  {comp.address || 'N/A'}
                                </Typography>
                                {comp.isCustom && (
                                  <Chip
                                    label='Custom'
                                    size='small'
                                    sx={{
                                      height: '20px',
                                      fontSize: '0.625rem',
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#6B7280',
                                }}
                              >
                                {comp.bed ?? 'N/A'} BR · {comp.bath ?? 'N/A'} BA
                                {comp.sqft
                                  ? ` · ${comp.sqft} sqft`
                                  : ' · N/A sqft'}
                              </Typography>
                            </Box>
                            <Chip
                              label={(() => {
                                const similarity = calculateSimilarity(comp);
                                return `${(similarity * 100).toFixed(
                                  0,
                                )}% match`;
                              })()}
                              size='small'
                              variant='outlined'
                              sx={{
                                fontSize: '0.75rem',
                              }}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(3, 1fr)',
                              gap: '1rem',
                              fontSize: '0.75rem',
                            }}
                          >
                            <Box>
                              <Typography sx={{ color: '#6B7280' }}>
                                Rent
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: '#111827',
                                }}
                              >
                                {comp.rent ? `$${comp.rent}` : 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ color: '#6B7280' }}>
                                $/SF
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: '#111827',
                                }}
                              >
                                {comp.rentPsf
                                  ? `$${comp.rentPsf.toFixed(2)}`
                                  : 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ color: '#6B7280' }}>
                                Distance
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: '#111827',
                                }}
                              >
                                {comp.distance ? `${comp.distance} mi` : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              paddingTop: '0.5rem',
                            }}
                          >
                            <Button
                              size='small'
                              variant='text'
                              onClick={() => {
                                setSelectedCompDetail(comp);
                                setShowCompDetail(true);
                              }}
                              startIcon={<Eye size={12} />}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                color: '#374151',
                              }}
                            >
                              View Details
                            </Button>
                            <Button
                              size='small'
                              variant='text'
                              onClick={() => handleRemoveComp(comp)}
                              startIcon={<Trash2 size={12} />}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                color: '#EF4444',
                                '&:hover': {
                                  backgroundColor: '#FEE2E2',
                                },
                              }}
                            >
                              Remove
                            </Button>
                            <Button
                              size='small'
                              variant='text'
                              onClick={() => handleReplaceComp(comp)}
                              startIcon={<RefreshCw size={12} />}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                color: '#374151',
                              }}
                            >
                              Replace
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    ))}

                    {loadingMore && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          paddingY: '2rem',
                        }}
                      >
                        <CircularProgress size={24} sx={{ color: '#39585C' }} />
                      </Box>
                    )}

                    {hasMoreComps && !loadingMore && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          paddingY: '1rem',
                        }}
                      >
                        <Button
                          variant='outlined'
                          onClick={loadMoreComps}
                          sx={{
                            textTransform: 'none',
                            borderColor: '#D1D5DB',
                            color: '#374151',
                            '&:hover': {
                              borderColor: '#39585C',
                              backgroundColor: '#F3F4F6',
                            },
                          }}
                        >
                          Load More ({totalCompsCount - comps.length} remaining)
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {!loading && !error && activeTab === 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.5rem',
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Unit Attributes
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                      }}
                    >
                      {[
                        {
                          label: 'Building',
                          value: opportunity.building || 'N/A',
                        },
                        {
                          label: 'Unit Number',
                          value: opportunity.unit || 'N/A',
                        },
                        { label: 'Bedrooms', value: opportunity.bed ?? 'N/A' },
                        {
                          label: 'Bathrooms',
                          value: opportunity.bath ?? 'N/A',
                        },
                        {
                          label: 'Square Feet',
                          value: opportunity.sqft || 'N/A',
                        },
                        { label: 'Floor', value: 'N/A' },
                        { label: 'Condition', value: 'N/A' },
                      ].map(item => (
                        <Box
                          key={item.label}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '0.875rem',
                          }}
                        >
                          <Typography sx={{ color: '#6B7280' }}>
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: '#111827',
                            }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Building Attributes
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                      }}
                    >
                      {[
                        {
                          label: 'Year Built',
                          value: opportunity.yearBuilt || 'N/A',
                        },
                        // { label: 'Property Class', value: 'N/A' },
                        {
                          label: 'Total Units',
                          value: opportunity.totalUnits || 'N/A',
                        },
                      ].map(item => (
                        <Box
                          key={item.label}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '0.875rem',
                          }}
                        >
                          <Typography sx={{ color: '#6B7280' }}>
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: '#111827',
                            }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Amenities
                    </Typography>
                    <Box
                      sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
                    >
                      {opportunity.amenities &&
                      Array.isArray(opportunity.amenities) &&
                      opportunity.amenities.length > 0 ? (
                        opportunity.amenities.map((amenity, index) => (
                          <Chip
                            key={index}
                            label={amenity}
                            size='small'
                            sx={{
                              fontSize: '0.75rem',
                              backgroundColor: '#F3F4F6',
                              color: '#111827',
                            }}
                          />
                        ))
                      ) : (
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#6B7280',
                            fontStyle: 'italic',
                          }}
                        >
                          N/A
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {!loading && !error && activeTab === 2 && (
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '1rem',
                      }}
                    >
                      Pricing History
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                      }}
                    >
                      {history.length > 0 ? (
                        history.map((entry, index) => (
                          <Box
                            key={index}
                            sx={{
                              position: 'relative',
                              paddingLeft: '1.5rem',
                              paddingBottom:
                                index < history.length - 1 ? '1rem' : 0,
                              borderLeft:
                                index < history.length - 1
                                  ? '2px solid #E5E7EB'
                                  : 'none',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                transform: 'translateX(-50%)',
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: '#39585C',
                                border: '2px solid #FFFFFF',
                              }}
                            />
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Chip
                                  label={entry.action}
                                  size='small'
                                  sx={{
                                    backgroundColor:
                                      entry.action === 'Approved'
                                        ? '#39585C'
                                        : '#F3F4F6',
                                    color:
                                      entry.action === 'Approved'
                                        ? '#FFFFFF'
                                        : '#374151',
                                    fontSize: '0.75rem',
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: '#6B7280',
                                  }}
                                >
                                  {entry.date}
                                </Typography>
                              </Box>
                              <Box sx={{ fontSize: '0.875rem' }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '0.5rem',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      color: '#111827',
                                    }}
                                  >
                                    ${entry.target}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: '0.75rem',
                                      color: '#10B981',
                                    }}
                                  >
                                    +${entry.delta}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#6B7280',
                                }}
                              >
                                <Typography>By {entry.user}</Typography>
                                <Typography
                                  sx={{
                                    marginTop: '0.25rem',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  {entry.rationale}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#6B7280',
                            fontStyle: 'italic',
                            padding: '1rem',
                            textAlign: 'center',
                          }}
                        >
                          No pricing history available
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      paddingTop: '1rem',
                      borderTop: '1px solid #E5E7EB',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Performance
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                      }}
                    >
                      {[
                        { label: 'Last Target', value: 'N/A' },
                        {
                          label: 'Achieved',
                          value: 'N/A',
                          color: '#6B7280',
                        },
                        { label: 'Variance', value: 'N/A' },
                      ].map(item => (
                        <Box
                          key={item.label}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '0.875rem',
                          }}
                        >
                          <Typography sx={{ color: '#6B7280' }}>
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: item.color || '#111827',
                            }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={showCompDetail}
        onClose={() => setShowCompDetail(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Comparable Unit Details</DialogTitle>
        <DialogContent>
          {selectedCompDetail && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
                paddingTop: '1rem',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1.5rem',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '0.75rem',
                    }}
                  >
                    Unit Information
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                  >
                    {[
                      {
                        label: 'Bedrooms',
                        value: selectedCompDetail.bed ?? 'N/A',
                      },
                      {
                        label: 'Bathrooms',
                        value: selectedCompDetail.bath ?? 'N/A',
                      },
                      {
                        label: 'Square Feet',
                        value: selectedCompDetail.sqft || 'N/A',
                      },
                      {
                        label: 'Rent',
                        value: selectedCompDetail.rent
                          ? `$${selectedCompDetail.rent}`
                          : 'N/A',
                      },
                      {
                        label: 'Rent per SF',
                        value: selectedCompDetail.rentPsf
                          ? `$${selectedCompDetail.rentPsf.toFixed(2)}`
                          : 'N/A',
                      },
                    ].map(item => (
                      <Box
                        key={item.label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.875rem',
                        }}
                      >
                        <Typography sx={{ color: '#6B7280' }}>
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: '#111827',
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '0.75rem',
                    }}
                  >
                    Comparison Metrics
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                  >
                    {[
                      {
                        label: 'Distance',
                        value: selectedCompDetail.distance
                          ? `${selectedCompDetail.distance} mi`
                          : 'N/A',
                      },
                      {
                        label: 'Similarity Score',
                        value: (() => {
                          const similarity =
                            calculateSimilarity(selectedCompDetail);
                          return `${(similarity * 100).toFixed(0)}%`;
                        })(),
                      },
                      {
                        label: 'Units in Building',
                        value: selectedCompDetail.totalUnits
                          ? selectedCompDetail.totalUnits.toString()
                          : 'N/A',
                      },
                      {
                        label: 'Year Constructed',
                        value: selectedCompDetail.yearBuilt
                          ? selectedCompDetail.yearBuilt.toString()
                          : 'N/A',
                      },
                    ].map(item => (
                      <Box
                        key={item.label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.875rem',
                        }}
                      >
                        <Typography sx={{ color: '#6B7280' }}>
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: '#111827',
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#111827',
                    marginBottom: '0.75rem',
                  }}
                >
                  Amenities
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {selectedCompDetail.amenities &&
                  Array.isArray(selectedCompDetail.amenities) &&
                  selectedCompDetail.amenities.length > 0 ? (
                    selectedCompDetail.amenities.map((amenity, index) => (
                      <Chip
                        key={index}
                        label={amenity}
                        size='small'
                        sx={{
                          fontSize: '0.75rem',
                        }}
                      />
                    ))
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: '#6B7280',
                        fontStyle: 'italic',
                      }}
                    >
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  height: '192px',
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                }}
              >
                <CompDetailMap
                  comp={selectedCompDetail}
                  sourceBuilding={sourceBuilding}
                  getAccessToken={getAccessToken}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCompDetail(false)}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setReportGenerating(false);
          setReportReady(false);
          setPdfLink(null);
        }}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {reportGenerating
            ? 'Generating Report...'
            : reportReady
            ? 'Report Ready'
            : 'Generate Report'}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '1rem',
            }}
          >
            {reportGenerating
              ? 'Creating comprehensive pricing justification report'
              : reportReady
              ? 'Your report is ready to download or share'
              : 'Generate a detailed report for this pricing recommendation'}
          </Typography>

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              paddingTop: '1rem',
              minHeight: '180px',
            }}
          >
            {reportGenerating && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    animation: `${spin} 1s linear infinite`,
                    display: 'inline-flex',
                  }}
                >
                  <Loader2 size={48} color='#39585C' />
                </Box>
                <Box
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    Analyzing comparable units...
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                    }}
                  >
                    This will take a few moments
                  </Typography>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                padding: '1rem',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                Report Contents:
              </Typography>
              <Box
                component='ul'
                sx={{
                  fontSize: '0.75rem',
                  color: '#6B7280',
                  margin: 0,
                  paddingLeft: '1.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                }}
              >
                <li>Unit details and current pricing</li>
                <li>{activeComps.length} comparable units analysis</li>
                <li>Market positioning and recommendations</li>
                <li>Amenity adjustments breakdown</li>
                <li>Historical pricing performance</li>
              </Box>
            </Box>
          </Box>

          <DialogActions sx={{ paddingTop: '1rem' }}>
            <Button
              onClick={() => {
                setShowReportModal(false);
                setReportGenerating(false);
                setReportReady(false);
                setPdfLink(null);
              }}
              sx={{
                textTransform: 'none',
                color: '#374151',
              }}
            >
              {reportReady ? 'Close' : 'Cancel'}
            </Button>
            <Button
              variant='contained'
              disabled={reportGenerating}
              startIcon={reportReady ? <Download size={16} /> : undefined}
              onClick={() => {
                if (reportReady) {
                  if (pdfLink) {
                    window.open(pdfLink, '_blank');
                  }
                } else {
                  handleGenerateReport();
                }
              }}
              sx={{
                backgroundColor: '#39585C',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#2A3F42' },
              }}
            >
              {reportGenerating
                ? 'Generating...'
                : reportReady
                ? 'Download PDF Report'
                : 'Generate Report'}
            </Button>
            <Button
              variant='outlined'
              startIcon={<Link2 size={16} />}
              onClick={() => {
                if (opportunity && rentGridId) {
                  const url = `${
                    window.location.origin
                  }/shared-unit/${rentGridId}/${encodeURIComponent(
                    opportunity.unit,
                  )}`;
                  setShareUrl(url);
                  setShowShareModal(true);
                }
              }}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
            >
              Generate Shareable Link
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Shareable Link</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '1rem',
            }}
          >
            Copy this link to share the unit view. Anyone with this link can
            view the unit details and map.
          </Typography>
          <TextField
            fullWidth
            value={shareUrl || ''}
            InputProps={{
              readOnly: true,
            }}
            onClick={e => e.target.select()}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                fontFamily: 'monospace',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowShareModal(false)}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              if (shareUrl) {
                navigator.clipboard.writeText(shareUrl);
                alert('Link copied to clipboard!');
              }
            }}
            sx={{
              backgroundColor: '#39585C',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#2A3F42' },
            }}
          >
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAddCustomComp}
        onClose={() => {
          setShowAddCustomComp(false);
          setCustomCompSearch('');
          setReplaceMode(false);
          setCompToReplace(null);
          setNearbyBuildingsPage(1);
        }}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {replaceMode ? 'Replace Comparable' : 'Add Custom Comparable'}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '1rem',
            }}
          >
            {replaceMode
              ? 'Search and select a property to replace the selected comparable'
              : 'Search and select properties to add as custom comparables'}
          </Typography>

          <Tabs
            value={customCompTab}
            onChange={(e, newValue) => setCustomCompTab(newValue)}
            sx={{
              marginTop: '1rem',
              marginBottom: '1rem',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                flex: 1,
              },
            }}
          >
            <Tab label='List View' value='list' />
            <Tab label='Map View' value='map' />
          </Tabs>

          {customCompTab === 'list' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Box sx={{ position: 'relative' }}>
                <Search
                  size={16}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6B7280',
                  }}
                />
                <TextField
                  fullWidth
                  placeholder='Search by address...'
                  value={customCompSearch}
                  onChange={e => setCustomCompSearch(e.target.value)}
                  sx={{
                    '& .MuiInputBase-root': {
                      paddingLeft: '2.5rem',
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  maxHeight: '384px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
                onScroll={handleNearbyBuildingsScroll}
              >
                {filteredProperties.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      paddingY: '2rem',
                      color: '#6B7280',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      No properties found
                    </Typography>
                  </Box>
                ) : (
                  filteredProperties.map(prop => (
                    <Box
                      key={prop.id}
                      sx={{
                        padding: '1rem',
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.5rem',
                        '&:hover': {
                          backgroundColor: '#F9FAFB',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'start',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: '#111827',
                            }}
                          >
                            {prop.address}
                          </Typography>
                          {prop.totalUnits && (
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: '#6B7280',
                                marginTop: '0.25rem',
                              }}
                            >
                              {prop.totalUnits} units
                            </Typography>
                          )}
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(1, 1fr)',
                              gap: '1rem',
                              marginTop: '0.75rem',
                              fontSize: '0.75rem',
                            }}
                          >
                            <Box>
                              <Typography sx={{ color: '#6B7280' }}>
                                Distance
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: '#111827',
                                }}
                              >
                                {prop.distance ? `${prop.distance} mi` : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Button
                          size='small'
                          variant='contained'
                          onClick={() =>
                            replaceMode
                              ? handleSelectBuildingForReplace(prop)
                              : handleAddCustomComp(prop)
                          }
                          disabled={addingComp}
                          startIcon={<Plus size={12} />}
                          sx={{
                            backgroundColor: '#39585C',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#2A3F42' },
                          }}
                        >
                          {replaceMode ? 'Select' : 'Add'}
                        </Button>
                      </Box>
                    </Box>
                  ))
                )}

                {loadingMoreNearbyBuildings && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingY: '1rem',
                    }}
                  >
                    <CircularProgress size={20} sx={{ color: '#39585C' }} />
                  </Box>
                )}

                {hasMoreNearbyBuildings && !loadingMoreNearbyBuildings && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      paddingY: '0.5rem',
                    }}
                  >
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={loadMoreNearbyBuildings}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#D1D5DB',
                        color: '#374151',
                        fontSize: '0.75rem',
                        '&:hover': {
                          borderColor: '#39585C',
                          backgroundColor: '#F3F4F6',
                        },
                      }}
                    >
                      Load More (
                      {totalNearbyBuildingsCount - nearbyBuildings.length}{' '}
                      remaining)
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {customCompTab === 'map' && (
            <Box>
              <Box
                sx={{
                  height: '384px',
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                }}
              >
                <AddCustomCompMap
                  sourceBuilding={sourceBuilding}
                  nearbyBuildings={nearbyBuildings}
                  existingCompBuildingIds={existingCompBuildingIds}
                  replaceMode={replaceMode}
                  compToReplace={compToReplace}
                  onSelectBuilding={building => {
                    if (replaceMode) {
                      handleSelectBuildingForReplace(building);
                    } else {
                      handleAddCustomComp(building);
                    }
                  }}
                  getAccessToken={getAccessToken}
                />
              </Box>

              <Box
                sx={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '0.5rem',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                  }}
                >
                  Click on blue markers to add properties as custom comparables.
                  Red marker shows your subject property.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddCustomComp(false);
              setCustomCompSearch('');
              setReplaceMode(false);
              setCompToReplace(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showConfirmDialog}
        onClose={() => {
          if (!addingComp && !checkingMatchingUnits) {
            setShowConfirmDialog(false);
            setSelectedBuildingToAdd(null);
            setReplaceMode(false);
            setCompToReplace(null);
            setMatchingUnitsData(null);
          }
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {replaceMode
            ? 'Confirm Replace Comparable'
            : 'Confirm Add Custom Comparable'}
        </DialogTitle>
        <DialogContent>
          {replaceMode && compToReplace && (
            <Box
              sx={{
                padding: '1rem',
                backgroundColor: '#FEF3C7',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#92400E',
                  marginBottom: '0.5rem',
                }}
              >
                Replacing:
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#111827',
                }}
              >
                {compToReplace.address}
              </Typography>
            </Box>
          )}
          {selectedBuildingToAdd && (
            <Box
              sx={{
                padding: '1rem',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#111827',
                }}
              >
                {selectedBuildingToAdd.address}
              </Typography>
              {selectedBuildingToAdd.buildingName && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    marginTop: '0.25rem',
                  }}
                >
                  {selectedBuildingToAdd.buildingName}
                </Typography>
              )}
              {selectedBuildingToAdd.distance && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    marginTop: '0.25rem',
                  }}
                >
                  Distance: {selectedBuildingToAdd.distance} mi
                </Typography>
              )}
            </Box>
          )}

          {checkingMatchingUnits && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingY: '2rem',
                gap: '0.75rem',
              }}
            >
              <CircularProgress size={20} sx={{ color: '#39585C' }} />
              <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                Checking for matching units...
              </Typography>
            </Box>
          )}

          {!checkingMatchingUnits && matchingUnitsData && (
            <Box sx={{ marginTop: '0.5rem' }}>
              {matchingUnitsData.hasMatchingUnits ? (
                <Box
                  sx={{
                    padding: '1rem',
                    backgroundColor: '#D1FAE5',
                    borderRadius: '0.5rem',
                    border: '1px solid #10B981',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <Check size={16} color='#059669' />
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#059669',
                      }}
                    >
                      {matchingUnitsData.matchingUnitsCount} Matching Unit
                      {matchingUnitsData.matchingUnitsCount > 1 ? 's' : ''}{' '}
                      Found
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#065F46',
                      marginBottom: '0.75rem',
                    }}
                  >
                    Units matching {matchingUnitsData.targetCriteria.bedrooms}{' '}
                    BR / {matchingUnitsData.targetCriteria.bathrooms} BA:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                  >
                    {matchingUnitsData.matchingUnits
                      .slice(0, 5)
                      .map((unit, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                          }}
                        >
                          <Typography sx={{ color: '#065F46' }}>
                            Unit {unit.unitNumber}
                            {unit.squareFeet
                              ? ` · ${unit.squareFeet} sqft`
                              : ''}
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 600, color: '#065F46' }}
                          >
                            ${unit.rent.toLocaleString()}
                            {unit.rentPsf ? ` ($${unit.rentPsf}/sf)` : ''}
                          </Typography>
                        </Box>
                      ))}
                    {matchingUnitsData.matchingUnitsCount > 5 && (
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#065F46',
                          fontStyle: 'italic',
                        }}
                      >
                        +{matchingUnitsData.matchingUnitsCount - 5} more unit
                        {matchingUnitsData.matchingUnitsCount - 5 > 1
                          ? 's'
                          : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    padding: '1rem',
                    backgroundColor: '#FEE2E2',
                    borderRadius: '0.5rem',
                    border: '1px solid #EF4444',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <X size={16} color='#DC2626' />
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#DC2626',
                      }}
                    >
                      No Matching Units Found
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#991B1B',
                    }}
                  >
                    This building has no units matching{' '}
                    {matchingUnitsData.targetCriteria.bedrooms} BR /{' '}
                    {matchingUnitsData.targetCriteria.bathrooms} BA criteria.
                    Adding it as a comparable will not contribute to rent
                    analysis.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {!checkingMatchingUnits && !matchingUnitsData && (
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                marginTop: '0.5rem',
              }}
            >
              {replaceMode
                ? 'Are you sure you want to replace the selected comparable with this building?'
                : 'Are you sure you want to add this building as a custom comparable?'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowConfirmDialog(false);
              setSelectedBuildingToAdd(null);
              setMatchingUnitsData(null);
            }}
            disabled={addingComp || checkingMatchingUnits}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={confirmAddCustomComp}
            disabled={addingComp || checkingMatchingUnits}
            sx={{
              backgroundColor:
                matchingUnitsData && !matchingUnitsData.hasMatchingUnits
                  ? '#EF4444'
                  : '#39585C',
              textTransform: 'none',
              '&:hover': {
                backgroundColor:
                  matchingUnitsData && !matchingUnitsData.hasMatchingUnits
                    ? '#DC2626'
                    : '#2A3F42',
              },
            }}
          >
            {addingComp
              ? replaceMode
                ? 'Replacing...'
                : 'Adding...'
              : checkingMatchingUnits
              ? 'Checking...'
              : matchingUnitsData && !matchingUnitsData.hasMatchingUnits
              ? 'Add Anyway'
              : replaceMode
              ? 'Confirm Replace'
              : 'Confirm Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRemoveConfirmDialog}
        onClose={() => {
          if (!removingComp) {
            setShowRemoveConfirmDialog(false);
            setCompToRemove(null);
          }
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Remove Comparable Unit</DialogTitle>
        <DialogContent>
          {compToRemove && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                paddingTop: '1rem',
              }}
            >
              <Box
                sx={{
                  padding: '1rem',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '0.5rem',
                  marginBottom: '0.5rem',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#111827',
                  }}
                >
                  {compToRemove.address || 'N/A'}
                </Typography>
                {compToRemove.bed && compToRemove.bath && (
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      marginTop: '0.25rem',
                    }}
                  >
                    {compToRemove.bed} BR · {compToRemove.bath} BA
                  </Typography>
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                }}
              >
                Are you sure you want to remove this comparable unit? This will
                recalculate the rent statistics.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowRemoveConfirmDialog(false);
              setCompToRemove(null);
            }}
            disabled={removingComp}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={confirmRemoveComp}
            disabled={removingComp}
            sx={{
              backgroundColor: '#EF4444',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#DC2626' },
            }}
          >
            {removingComp ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AddCustomCompMap = ({
  sourceBuilding,
  nearbyBuildings,
  existingCompBuildingIds,
  replaceMode,
  compToReplace,
  onSelectBuilding,
  getAccessToken,
}) => {
  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const filteredBuildings = nearbyBuildings.filter(building => {
    const isAlreadyComp = existingCompBuildingIds.includes(building.id);
    if (replaceMode && compToReplace) {
      return building.id !== compToReplace.buildingId && !isAlreadyComp;
    }
    return !isAlreadyComp;
  });

  useEffect(() => {
    let isMounted = true;
    const fetchToken = async () => {
      try {
        setLoading(true);
        setError(null);
        const accessToken = await getAccessToken();
        const url = `${process.env.REACT_APP_NODE_URL}/admin/token`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch ArcGIS token');
        }
        const data = await response.json();
        if (data?.token?.access_token && isMounted) {
          setToken(data.token.access_token);
        } else if (isMounted) {
          throw new Error('ArcGIS token not found in response');
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch ArcGIS token');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchToken();

    return () => {
      isMounted = false;
    };
  }, [getAccessToken]);

  useEffect(() => {
    if (
      !token ||
      !sourceBuilding ||
      !sourceBuilding.location ||
      !mapRef.current
    ) {
      return;
    }

    let view;

    const initializeMap = async () => {
      try {
        const [Map, MapView, Graphic, GraphicsLayer, esriConfig] =
          await loadModules([
            'esri/Map',
            'esri/views/MapView',
            'esri/Graphic',
            'esri/layers/GraphicsLayer',
            'esri/config',
          ]);

        esriConfig.apiKey = token;

        const graphicsLayer = new GraphicsLayer();

        const map = new Map({
          basemap: 'gray-vector',
          layers: [graphicsLayer],
        });

        const center = [
          sourceBuilding.location.lng,
          sourceBuilding.location.lat,
        ];

        view = new MapView({
          container: mapRef.current,
          map,
          center,
          zoom: 14,
        });

        viewRef.current = view;

        const subjectGraphic = new Graphic({
          geometry: {
            type: 'point',
            longitude: sourceBuilding.location.lng,
            latitude: sourceBuilding.location.lat,
          },
          symbol: {
            type: 'simple-marker',
            style: 'circle',
            color: '#EF4444',
            size: '14px',
            outline: {
              color: '#FFFFFF',
              width: 2,
            },
          },
          attributes: {
            type: 'subject',
          },
        });

        graphicsLayer.add(subjectGraphic);

        const buildingGraphics = filteredBuildings
          .filter(b => b.location && b.location.lng && b.location.lat)
          .map(building => {
            return new Graphic({
              geometry: {
                type: 'point',
                longitude: building.location.lng,
                latitude: building.location.lat,
              },
              symbol: {
                type: 'simple-marker',
                style: 'circle',
                color: '#3B82F6',
                size: '12px',
                outline: {
                  color: '#FFFFFF',
                  width: 2,
                },
              },
              attributes: {
                type: 'building',
                id: building.id,
                address: building.address,
                totalUnits: building.totalUnits,
                distance: building.distance,
              },
            });
          });

        if (buildingGraphics.length > 0) {
          graphicsLayer.addMany(buildingGraphics);
        }

        view.on('click', async event => {
          const response = await view.hitTest(event);
          if (response.results.length > 0) {
            const graphic = response.results.find(
              r => r.graphic?.attributes?.type === 'building',
            );
            if (graphic) {
              const buildingId = graphic.graphic.attributes.id;
              const building = filteredBuildings.find(b => b.id === buildingId);
              if (building) {
                onSelectBuilding(building);
              }
            }
          }
        });

        view.on('pointer-move', async event => {
          const response = await view.hitTest(event);
          const hasBuildingGraphic = response.results.some(
            r => r.graphic?.attributes?.type === 'building',
          );
          view.container.style.cursor = hasBuildingGraphic
            ? 'pointer'
            : 'default';
        });
      } catch (err) {
        setError(err.message || 'Failed to load map');
      }
    };

    initializeMap();

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, [token, sourceBuilding, filteredBuildings, onSelectBuilding]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      {(!token || loading) && !error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={24} sx={{ color: '#39585C' }} />
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#6B7280',
              }}
            >
              Loading map...
            </Typography>
          </Box>
        </Box>
      )}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            zIndex: 2,
            padding: '0.75rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#EF4444',
              textAlign: 'center',
            }}
          >
            {error}
          </Typography>
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};

const CompDetailMap = ({ comp, sourceBuilding, getAccessToken }) => {
  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchToken = async () => {
      try {
        setLoading(true);
        setError(null);
        const accessToken = await getAccessToken();
        const url = `${process.env.REACT_APP_NODE_URL}/admin/token`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch ArcGIS token');
        }
        const data = await response.json();
        if (data?.token?.access_token && isMounted) {
          setToken(data.token.access_token);
        } else if (isMounted) {
          throw new Error('ArcGIS token not found in response');
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch ArcGIS token');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchToken();

    return () => {
      isMounted = false;
    };
  }, [getAccessToken]);

  useEffect(() => {
    if (!token || !comp || !comp.location || !mapRef.current) {
      return;
    }

    let view;

    const initializeMap = async () => {
      try {
        const [Map, MapView, Graphic, GraphicsLayer, esriConfig] =
          await loadModules([
            'esri/Map',
            'esri/views/MapView',
            'esri/Graphic',
            'esri/layers/GraphicsLayer',
            'esri/config',
          ]);

        esriConfig.apiKey = token;

        const graphicsLayer = new GraphicsLayer();

        const map = new Map({
          basemap: 'gray-vector',
          layers: [graphicsLayer],
        });

        const center = [comp.location.lng, comp.location.lat];

        view = new MapView({
          container: mapRef.current,
          map,
          center,
          zoom: 15,
          navigation: {
            mouseWheelZoomEnabled: false,
          },
        });

        viewRef.current = view;

        const compGraphic = new Graphic({
          geometry: {
            type: 'point',
            longitude: comp.location.lng,
            latitude: comp.location.lat,
          },
          symbol: {
            type: 'simple-marker',
            style: 'circle',
            color: comp.isCustom ? '#3B82F6' : '#10B981',
            size: '12px',
            outline: {
              color: '#FFFFFF',
              width: 2,
            },
          },
        });

        graphicsLayer.add(compGraphic);

        if (sourceBuilding?.location?.lng && sourceBuilding?.location?.lat) {
          const subjectGraphic = new Graphic({
            geometry: {
              type: 'point',
              longitude: sourceBuilding.location.lng,
              latitude: sourceBuilding.location.lat,
            },
            symbol: {
              type: 'simple-marker',
              style: 'circle',
              color: '#EF4444',
              size: '10px',
              outline: {
                color: '#FFFFFF',
                width: 1,
              },
            },
          });

          graphicsLayer.add(subjectGraphic);
        }
      } catch (err) {
        setError(err.message || 'Failed to load map');
      }
    };

    initializeMap();

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, [token, comp, sourceBuilding]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      {(!token || loading) && !error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={24} sx={{ color: '#39585C' }} />
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#6B7280',
              }}
            >
              Loading map...
            </Typography>
          </Box>
        </Box>
      )}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            zIndex: 2,
            padding: '0.75rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#EF4444',
              textAlign: 'center',
            }}
          >
            {error}
          </Typography>
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};

const CompsMap = ({ sourceBuilding, comps, getAccessToken }) => {
  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchToken = async () => {
      try {
        setLoading(true);
        setError(null);
        const accessToken = await getAccessToken();
        const url = `${process.env.REACT_APP_NODE_URL}/admin/token`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch ArcGIS token');
        }
        const data = await response.json();
        if (data?.token?.access_token && isMounted) {
          setToken(data.token.access_token);
        } else if (isMounted) {
          throw new Error('ArcGIS token not found in response');
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch ArcGIS token');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchToken();

    return () => {
      isMounted = false;
    };
  }, [getAccessToken]);

  useEffect(() => {
    if (
      !token ||
      !sourceBuilding ||
      !sourceBuilding.location ||
      !mapRef.current
    ) {
      return;
    }

    let view;

    const initializeMap = async () => {
      try {
        const [Map, MapView, Graphic, GraphicsLayer, esriConfig] =
          await loadModules([
            'esri/Map',
            'esri/views/MapView',
            'esri/Graphic',
            'esri/layers/GraphicsLayer',
            'esri/config',
          ]);

        esriConfig.apiKey = token;

        const graphicsLayer = new GraphicsLayer();

        const map = new Map({
          basemap: 'gray-vector',
          layers: [graphicsLayer],
        });

        const center = [
          sourceBuilding.location.lng,
          sourceBuilding.location.lat,
        ];

        view = new MapView({
          container: mapRef.current,
          map,
          center,
          zoom: 14,
          navigation: {
            mouseWheelZoomEnabled: false,
          },
        });

        viewRef.current = view;

        const subjectGraphic = new Graphic({
          geometry: {
            type: 'point',
            longitude: sourceBuilding.location.lng,
            latitude: sourceBuilding.location.lat,
          },
          symbol: {
            type: 'simple-marker',
            style: 'circle',
            color: '#EF4444',
            size: '10px',
            outline: {
              color: '#FFFFFF',
              width: 1,
            },
          },
        });

        graphicsLayer.add(subjectGraphic);

        const compGraphics = (comps || [])
          .filter(
            comp => comp.location && comp.location.lng && comp.location.lat,
          )
          .map(comp => {
            const isCustom = comp.isCustom;
            return new Graphic({
              geometry: {
                type: 'point',
                longitude: comp.location.lng,
                latitude: comp.location.lat,
              },
              symbol: {
                type: 'simple-marker',
                style: 'circle',
                color: isCustom ? '#3B82F6' : '#10B981',
                size: '8px',
                outline: {
                  color: '#FFFFFF',
                  width: 1,
                },
              },
              attributes: {
                id: comp.id,
                address: comp.address,
                rent: comp.rent,
                rentPsf: comp.rentPsf,
                distance: comp.distance,
                similarity: comp.similarity,
                isCustom,
              },
            });
          });

        if (compGraphics.length > 0) {
          graphicsLayer.addMany(compGraphics);
        }
      } catch (err) {
        setError(err.message || 'Failed to load map');
      }
    };

    initializeMap();

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, [token, sourceBuilding, comps]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      {(!token || loading) && !error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={24} sx={{ color: '#39585C' }} />
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#6B7280',
              }}
            >
              Loading map...
            </Typography>
          </Box>
        </Box>
      )}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            zIndex: 2,
            padding: '0.75rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#EF4444',
              textAlign: 'center',
            }}
          >
            {error}
          </Typography>
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};

export default InsightDrawer;
